import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { 
  ArrowLeft, Plus, Trash2, Edit2, Save, X, Quote, 
  GripVertical, Eye, EyeOff, User, Building2, MessageSquareQuote 
} from 'lucide-react';

const AdminTestimonialsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    title: '',
    institution: '',
    content: '',
    photo_url: '',
    active: true,
    order: 0
  });

  useEffect(() => {
    if (user?.role !== 'admin') {
      toast.error('Acesso negado');
      navigate('/dashboard');
      return;
    }
    fetchTestimonials();
  }, [user, navigate]);

  const fetchTestimonials = async () => {
    try {
      const response = await api.get('/api/testimonials/admin/all');
      setTestimonials(response.data.testimonials || []);
    } catch (error) {
      toast.error('Erro ao carregar depoimentos');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.content) {
      toast.error('Nome e depoimento são obrigatórios');
      return;
    }

    try {
      if (editingId) {
        await api.put(`/api/testimonials/${editingId}`, formData);
        toast.success('Depoimento atualizado!');
      } else {
        await api.post('/api/testimonials', formData);
        toast.success('Depoimento adicionado!');
      }
      
      resetForm();
      fetchTestimonials();
    } catch (error) {
      toast.error('Erro ao salvar depoimento');
    }
  };

  const handleEdit = (testimonial) => {
    setFormData({
      name: testimonial.name,
      title: testimonial.title || '',
      institution: testimonial.institution || '',
      content: testimonial.content,
      photo_url: testimonial.photo_url || '',
      active: testimonial.active,
      order: testimonial.order || 0
    });
    setEditingId(testimonial.testimonial_id);
    setShowForm(true);
  };

  const handleDelete = async (testimonialId) => {
    if (!window.confirm('Tem certeza que deseja excluir este depoimento?')) return;
    
    try {
      await api.delete(`/api/testimonials/${testimonialId}`);
      toast.success('Depoimento excluído');
      fetchTestimonials();
    } catch (error) {
      toast.error('Erro ao excluir');
    }
  };

  const toggleActive = async (testimonial) => {
    try {
      await api.put(`/api/testimonials/${testimonial.testimonial_id}`, {
        ...testimonial,
        active: !testimonial.active
      });
      toast.success(testimonial.active ? 'Depoimento desativado' : 'Depoimento ativado');
      fetchTestimonials();
    } catch (error) {
      toast.error('Erro ao atualizar');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      title: '',
      institution: '',
      content: '',
      photo_url: '',
      active: true,
      order: 0
    });
    setEditingId(null);
    setShowForm(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F9F8] flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-[#002FA7] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F9F8]">
      <header className="bg-white border-b border-[#E4E4E7]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/admin')}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold tracking-tight">Depoimentos</h1>
              <p className="text-sm text-gray-500">Gerencie os depoimentos exibidos na landing page</p>
            </div>
          </div>
          <Button 
            onClick={() => setShowForm(true)}
            className="bg-[#002FA7] hover:bg-[#001f75]"
          >
            <Plus className="h-4 w-4 mr-2" />
            Novo Depoimento
          </Button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {/* Form */}
        {showForm && (
          <div className="bg-white rounded-xl border border-[#E4E4E7] p-6 mb-8 shadow-sm">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-bold flex items-center gap-2">
                <MessageSquareQuote className="h-5 w-5 text-[#002FA7]" />
                {editingId ? 'Editar Depoimento' : 'Novo Depoimento'}
              </h2>
              <Button variant="ghost" size="icon" onClick={resetForm}>
                <X className="h-5 w-5" />
              </Button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Nome *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Nome completo da pessoa"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="title">Título/Cargo</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Ex: Professor Doutor, Pesquisador..."
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="institution">Instituição</Label>
                  <Input
                    id="institution"
                    value={formData.institution}
                    onChange={(e) => setFormData(prev => ({ ...prev, institution: e.target.value }))}
                    placeholder="Ex: Universidade Federal..."
                  />
                </div>
                <div>
                  <Label htmlFor="photo_url">URL da Foto (opcional)</Label>
                  <Input
                    id="photo_url"
                    type="url"
                    value={formData.photo_url}
                    onChange={(e) => setFormData(prev => ({ ...prev, photo_url: e.target.value }))}
                    placeholder="https://..."
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="content">Depoimento *</Label>
                <Textarea
                  id="content"
                  value={formData.content}
                  onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                  placeholder="O depoimento da pessoa sobre a plataforma..."
                  rows={4}
                  required
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="order">Ordem de Exibição</Label>
                  <Input
                    id="order"
                    type="number"
                    min="0"
                    value={formData.order}
                    onChange={(e) => setFormData(prev => ({ ...prev, order: parseInt(e.target.value) || 0 }))}
                  />
                  <p className="text-xs text-gray-500 mt-1">Números menores aparecem primeiro</p>
                </div>
                <div className="flex items-center gap-3 pt-6">
                  <input
                    type="checkbox"
                    id="active"
                    checked={formData.active}
                    onChange={(e) => setFormData(prev => ({ ...prev, active: e.target.checked }))}
                    className="h-4 w-4 rounded border-gray-300"
                  />
                  <Label htmlFor="active" className="font-normal">Ativo (visível na landing page)</Label>
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="submit" className="bg-[#002FA7] hover:bg-[#001f75]">
                  <Save className="h-4 w-4 mr-2" />
                  {editingId ? 'Salvar Alterações' : 'Adicionar Depoimento'}
                </Button>
                <Button type="button" variant="outline" onClick={resetForm}>
                  Cancelar
                </Button>
              </div>
            </form>
          </div>
        )}

        {/* Testimonials List */}
        <div className="space-y-4">
          {testimonials.length === 0 ? (
            <div className="bg-white rounded-xl border border-[#E4E4E7] p-12 text-center">
              <Quote className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-700 mb-2">Nenhum depoimento cadastrado</h3>
              <p className="text-gray-500 mb-4">Adicione depoimentos para exibir na página inicial</p>
              <Button onClick={() => setShowForm(true)} className="bg-[#002FA7] hover:bg-[#001f75]">
                <Plus className="h-4 w-4 mr-2" />
                Adicionar Primeiro Depoimento
              </Button>
            </div>
          ) : (
            testimonials.map((testimonial, index) => (
              <div 
                key={testimonial.testimonial_id}
                className={`bg-white rounded-xl border border-[#E4E4E7] p-6 transition-all ${
                  !testimonial.active ? 'opacity-60' : ''
                }`}
              >
                <div className="flex items-start gap-4">
                  {/* Drag handle */}
                  <div className="text-gray-300 pt-1">
                    <GripVertical className="h-5 w-5" />
                  </div>

                  {/* Photo */}
                  {testimonial.photo_url ? (
                    <img
                      src={testimonial.photo_url}
                      alt={testimonial.name}
                      className="w-14 h-14 rounded-full object-cover border-2 border-[#E4E4E7]"
                    />
                  ) : (
                    <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#002FA7] to-[#0040E0] text-white flex items-center justify-center text-lg font-bold">
                      {testimonial.name?.charAt(0).toUpperCase()}
                    </div>
                  )}

                  {/* Content */}
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h3 className="font-bold text-[#0A0A0A]">{testimonial.name}</h3>
                        <p className="text-sm text-[#002FA7]">{testimonial.title}</p>
                        {testimonial.institution && (
                          <p className="text-sm text-gray-500 flex items-center gap-1">
                            <Building2 className="h-3 w-3" />
                            {testimonial.institution}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {/* Status badge */}
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          testimonial.active 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-gray-100 text-gray-500'
                        }`}>
                          {testimonial.active ? 'Ativo' : 'Inativo'}
                        </span>
                        <span className="text-xs text-gray-400">#{testimonial.order || 0}</span>
                      </div>
                    </div>

                    <p className="text-[#4B4C52] italic mb-4">"{testimonial.content}"</p>

                    {/* Actions */}
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(testimonial)}
                      >
                        <Edit2 className="h-4 w-4 mr-1" />
                        Editar
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => toggleActive(testimonial)}
                      >
                        {testimonial.active ? (
                          <>
                            <EyeOff className="h-4 w-4 mr-1" />
                            Desativar
                          </>
                        ) : (
                          <>
                            <Eye className="h-4 w-4 mr-1" />
                            Ativar
                          </>
                        )}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(testimonial.testimonial_id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        Excluir
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Preview info */}
        {testimonials.filter(t => t.active).length > 0 && (
          <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              <strong>{testimonials.filter(t => t.active).length} depoimento(s) ativo(s)</strong> serão exibidos em carrossel na página inicial.
              O carrossel muda automaticamente a cada 5 segundos.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminTestimonialsPage;
